import { useState } from 'react';
import { reviews } from '../../../mocks/reviews';

export default function ReviewsSection() {
  const [selectedCasino, setSelectedCasino] = useState<string>('all');

  // Получаем уникальные казино из отзывов
  const casinos = ['all', ...Array.from(new Set(reviews.map(r => r.casino)))];

  // Фильтруем отзывы по выбранному казино
  const filteredReviews = selectedCasino === 'all' 
    ? reviews 
    : reviews.filter(r => r.casino === selectedCasino);

  // Функция для отображения звезд рейтинга
  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`ri-star-${star <= rating ? 'fill' : 'line'} text-lg ${
              star <= rating ? 'text-amber-400' : 'text-gray-300'
            }`}
          ></i>
        ))}
      </div>
    );
  };

  // Функция для форматирования даты
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Заголовок секции */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Отзывы игроков о мобильных казино онлайн
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Реальные отзывы верифицированных игроков о лучших онлайн казино России. 
            Узнайте о личном опыте других пользователей перед выбором казино.
          </p>
        </div>

        {/* Фильтр по казино */}
        <div className="mb-10 flex justify-center">
          <div className="inline-flex flex-wrap gap-2 bg-white p-2 rounded-xl shadow-sm">
            {casinos.map((casino) => (
              <button
                key={casino}
                onClick={() => setSelectedCasino(casino)}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-300 whitespace-nowrap ${
                  selectedCasino === casino
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {casino === 'all' ? 'Все казино' : casino}
              </button>
            ))}
          </div>
        </div>

        {/* Сетка отзывов */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReviews.map((review, index) => (
            <div
              key={review.id}
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Шапка карточки с аватаром и информацией */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
                  <img
                    src={review.avatar}
                    alt={review.playerName}
                    className="w-full h-full rounded-full object-cover border-2 border-teal-100"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {review.playerName}
                    </h3>
                    {review.verified && (
                      <div className="flex items-center justify-center w-5 h-5 flex-shrink-0">
                        <i className="ri-verified-badge-fill text-lg text-teal-600" title="Верифицированный игрок"></i>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{review.casino}</p>
                </div>
              </div>

              {/* Рейтинг */}
              <div className="mb-4">
                {renderStars(review.rating)}
              </div>

              {/* Текст отзыва */}
              <p className="text-gray-700 leading-relaxed mb-4 line-clamp-4">
                {review.reviewText}
              </p>

              {/* Дата отзыва */}
              <div className="flex items-center gap-2 text-sm text-gray-500 pt-4 border-t border-gray-100">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-base"></i>
                </div>
                <span>{formatDate(review.date)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Сообщение если нет отзывов */}
        {filteredReviews.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-5xl text-gray-400"></i>
            </div>
            <p className="text-gray-600 text-lg">
              Отзывов для выбранного казино пока нет
            </p>
          </div>
        )}

        {/* Призыв к действию */}
        <div className="mt-12 text-center bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Поделитесь своим опытом
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Помогите другим игрокам сделать правильный выбор. Оставьте честный отзыв 
            о вашем опыте игры в онлайн казино.
          </p>
          <button className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors duration-300 shadow-md hover:shadow-lg whitespace-nowrap">
            Оставить отзыв
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
          opacity: 0;
        }

        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}
